import {useState} from 'react'
import {Plus, Trash2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {useI18n} from '@/i18n'
import {useDialog} from '@/hooks/useDialog'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {DefinitionFields, type DefinitionFieldValues} from '@/components/attributes/DefinitionFields'
import {PatternSuggestDialog} from '@/components/attributes/PatternSuggestDialog'

interface AttributeDefinitionEditorProps {
    definitions: AttributeDefinition[]
    onAdd: (name: string) => void
    onUpdate: (definition: AttributeDefinition, body: Record<string, unknown>) => void
    onDelete: (definitionId: string) => void
    isAdding?: boolean
}

export function ParameterEditor({definitions, onAdd, onUpdate, onDelete, isAdding}: AttributeDefinitionEditorProps) {
    const i18n = useI18n()
    const [newName, setNewName] = useState('')
    const patternSuggestDialog = useDialog()
    const [patternSuggestDefinition, setPatternSuggestDefinition] = useState<AttributeDefinition | null>(null)

    function addParameter() {
        if (!newName.trim()) return
        const name = newName.trim()
        if (definitions.some(d => d.name === name)) return
        onAdd(name)
        setNewName('')
    }

    function handleSuggestPattern(definition: AttributeDefinition) {
        setPatternSuggestDefinition(definition)
        patternSuggestDialog.open()
    }

    function handlePatternAccept(pattern: string) {
        if (!patternSuggestDefinition) return
        const updates: Partial<DefinitionFieldValues> = {
            validationRules: {
                ...patternSuggestDefinition.validationRules,
                pattern,
            },
        }
        const body: Record<string, unknown> = {
            name: patternSuggestDefinition.name,
            displayName: patternSuggestDefinition.displayName,
            valueType: patternSuggestDefinition.valueType,
            operations: patternSuggestDefinition.operations,
            validationRules: updates.validationRules,
            required: patternSuggestDefinition.required,
            sortOrder: patternSuggestDefinition.sortOrder,
            active: patternSuggestDefinition.active,
            version: patternSuggestDefinition.version,
        }
        onUpdate(patternSuggestDefinition, body)
        patternSuggestDialog.close()
        setPatternSuggestDefinition(null)
    }

    return (
        <Stack gap="sm" data-testid="parameter-definition-editor">
            <Strong>{i18n.reporting.parameters.title}</Strong>

            {definitions.length > 0 && (
                <Stack gap="sm">
                    {definitions.map(param => (
                        <DefinitionRow key={param.id} definition={param} onUpdate={onUpdate} onDelete={() => onDelete(param.id)} onSuggestPattern={() => handleSuggestPattern(param)} />
                    ))}
                </Stack>
            )}

            <InlineRow gap="sm">
                <Input
                    data-testid="param-def-new-name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addParameter()}
                    placeholder={i18n.reporting.parameters.addPlaceholder}
                    className="w-48"
                />
                <Button variant="outline" size="sm" data-testid="btn-add-parameter"
                        onClick={addParameter} disabled={!newName.trim() || isAdding}>
                    <Icon icon={Plus} size="md" className="mr-1"/>{i18n.reporting.parameters.add}
                </Button>
            </InlineRow>

            <PatternSuggestDialog
                open={patternSuggestDialog.isOpen}
                onOpenChange={patternSuggestDialog.bind.onOpenChange}
                onPatternAccepted={handlePatternAccept}
            />
        </Stack>
    )
}

function DefinitionRow({definition, onUpdate, onDelete, onSuggestPattern}: {
    definition: AttributeDefinition
    onUpdate: (d: AttributeDefinition, u: Record<string, unknown>) => void
    onDelete: () => void
    onSuggestPattern: () => void
}) {
    const fieldValues: DefinitionFieldValues = {
        name: definition.name,
        displayName: definition.displayName,
        displayNameTranslations: {},
        valueType: definition.valueType,
        required: definition.required,
        active: definition.active,
        sortOrder: definition.sortOrder,
        validationRules: definition.validationRules,
        typeCodes: definition.typeCodes ?? [],
        searchable: definition.searchable,
        filterable: definition.filterable,
    }

    function handleChange(updates: Partial<DefinitionFieldValues>) {
        const body: Record<string, unknown> = {
            name: definition.name,
            displayName: updates.displayName ?? definition.displayName,
            valueType: updates.valueType ?? definition.valueType,
            operations: definition.operations,
            validationRules: updates.validationRules !== undefined ? updates.validationRules : definition.validationRules,
            required: updates.required ?? definition.required,
            sortOrder: updates.sortOrder ?? definition.sortOrder,
            active: updates.active ?? definition.active,
            version: definition.version,
        }
        onUpdate(definition, body)
    }

    return (
        <Card className="p-2" data-testid={`param-def-${definition.name}`}>
            <InlineRow gap="sm">
            <DefinitionFields
                values={fieldValues}
                onChange={handleChange}
                layout="horizontal"
                testIdPrefix={`param-def-${definition.name}`}  
                onSuggestPattern={onSuggestPattern}
            />
            <Button variant="ghost" size="icon" data-testid={`param-def-remove-${definition.name}`} onClick={onDelete}>
                <Trash2 className="h-4 w-4"/>
            </Button>
            </InlineRow>
        </Card>
    )
}
